import { Box, Button, Modal, SxProps, Theme, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/CloseRounded';
import { ComponentProps, CSSProperties, PropsWithChildren } from 'react';
import { LoadingButton } from '@mui/lab';
import { important } from '../../../utils/global/helper';
import DropdownMenu from '../DropdownMenu';

const style: SxProps<Theme> = {
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  overflowY: 'auto',
};

export interface FooterProps {
  button: {
    disabled?: boolean;
    loading?: boolean;
    type?: 'submit' | 'button';
    text: string;
    onClick?: () => void | Promise<void>;
  };
  /** Sits opposite the submit button, for removing what is being edited. */
  destructiveAction?: {
    label: string;
    onClick: () => void;
  };
}

interface Props extends PropsWithChildren {
  open: boolean;
  onClose: () => void;
  title?: string;
  /** One-line title with an ellipsis, for forms that show the full name below */
  truncateTitle?: boolean;
  footer?: FooterProps;
  hideCloseButton?: boolean;
  width?: CSSProperties['width'];
  options?: ComponentProps<typeof DropdownMenu>['options'];
}

export function Footer({ button, destructiveAction }: FooterProps) {
  return (
    <Box
      p={2}
      position="sticky"
      bottom={0}
      zIndex={1}
      boxSizing="border-box"
      width="100%"
      display="flex"
      alignItems="center"
      justifyContent={destructiveAction ? 'space-between' : 'flex-end'}
      gap={2}
      borderTop={theme => `0.5px solid ${theme.palette.lightOutlineGrey.main}`}
      bgcolor="background.paper">
      {destructiveAction && (
        <Button
          type="button"
          onClick={destructiveAction.onClick}
          sx={{
            minWidth: 0,
            padding: 0,
            textTransform: 'none',
            color: theme => theme.palette.error.dark,
            '&:hover': { background: 'transparent' },
            typography: 'caption',
            fontWeight: 600,
          }}>
          {destructiveAction.label}
        </Button>
      )}
      <LoadingButton
        type={button.type ?? 'submit'}
        className="primary"
        disabled={button.disabled}
        variant="contained"
        color="primary"
        sx={{
          height: '40px',
          whiteSpace: 'nowrap',
          borderRadius: '8px',
          fontSize: 'body2.fontSize',
          paddingX: important('3rem'),
        }}
        loading={button.loading}
        onClick={button.onClick}>
        <span>{button.text}</span>
      </LoadingButton>
    </Box>
  );
}

function SmallModal({
  open,
  onClose,
  title,
  children,
  footer,
  hideCloseButton,
  width = '410px',
  options,
  truncateTitle,
}: Props) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      disableAutoFocus>
      <Box
        position="absolute"
        top="50%"
        left="50%"
        borderRadius="16px"
        maxHeight="90%"
        boxShadow={24}
        width={{
          xs: '90%',
          md: width,
        }}
        sx={style}>
        {(title || !hideCloseButton || !!options?.length) && (
          <Box
            position="sticky"
            top={0}
            bgcolor="background.paper"
            zIndex={1}
            width="100%"
            boxSizing="border-box"
            py={1}
            display="flex"
            alignItems="center"
            justifyContent="center"
            minHeight="50px"
            borderBottom={theme =>
              `0.5px solid ${theme.palette.lightOutlineGrey.main}`
            }>
            {title && (
              // clears the close and options buttons on both sides so it stays centred
              <Typography
                textAlign="center"
                variant="h5"
                noWrap={truncateTitle}
                sx={{ px: 7, minWidth: 0 }}>
                {title || ''}
              </Typography>
            )}
            {!hideCloseButton && (
              <Box
                position="absolute"
                top={0}
                height="100%"
                display="flex"
                alignItems="center"
                // X stays top-right by default; moves to top-left only when an
                // options menu occupies the top-right (e.g. gift card details).
                sx={options?.length ? { left: 12 } : { right: 12 }}>
                <Button
                  variant="text"
                  onClick={onClose}
                  color="inherit"
                  sx={{ padding: 0, width: 'auto', minWidth: 0 }}>
                  <CloseIcon sx={() => ({ color: 'text.primary' })} />
                </Button>
              </Box>
            )}
            {!!options?.length && (
              <Box
                position="absolute"
                top={0}
                right={12}
                height="100%"
                display="flex"
                alignItems="center">
                <DropdownMenu options={options} />
              </Box>
            )}
          </Box>
        )}

        {children}

        {footer && <Footer {...footer} />}
      </Box>
    </Modal>
  );
}

export default SmallModal;
